/**
 * Tiny Vietnamese relative-time formatter.
 *
 * We deliberately avoid pulling `date-fns` (or similar) — the dashboard
 * only needs short Vietnamese strings like "2 giờ trước" or "3 ngày trước"
 * and the bundle savings are worth the ~20 lines of math.
 *
 * Supported buckets, mirroring colloquial Vietnamese on Apple platforms:
 *   - <60 s        → "vừa xong"
 *   - <60 min      → "{n} phút trước"
 *   - <24 h        → "{n} giờ trước"
 *   - <30 d        → "{n} ngày trước"
 *   - <12 mo       → "{n} tháng trước"
 *   - else         → "{n} năm trước"
 *
 * Future-dated inputs (clock skew between the Worker and the browser)
 * collapse to "vừa xong" so the UI never displays "trong 3 giây".
 */

export function formatRelativeTimeVi(
  isoTimestamp: string,
  now: Date = new Date(),
): string {
  const then = new Date(isoTimestamp);
  if (Number.isNaN(then.getTime())) {
    return '';
  }

  const diffMs = now.getTime() - then.getTime();
  // Treat future timestamps as "just now" rather than negative durations.
  const seconds = Math.max(0, Math.floor(diffMs / 1000));

  if (seconds < 60) {
    return 'vừa xong';
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} phút trước`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} giờ trước`;
  }
  const days = Math.floor(hours / 24);
  if (days < 30) {
    return `${days} ngày trước`;
  }
  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months} tháng trước`;
  }
  const years = Math.floor(days / 365);
  return `${years} năm trước`;
}
